import React, { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkContext } from "../../LinkProvider";
import AnimatedCTA from "../AnimatedCTA";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import securityBulb from "../../img/experience/security-bulb.webp";
import securityArrowLeft from "../../img/experience/security-arrow-left.svg";
import securityArrowRight from "../../img/experience/security-arrow-right.svg";

const CARDS = [
  {
    numKey: "exp.security.c1.num",
    titleKey: "exp.security.c1.title",
    descKey: "exp.security.c1.desc",
    hasBulb: true,
  },
  {
    numKey: "exp.security.c2.num",
    titleKey: "exp.security.c2.title",
    descKey: "exp.security.c2.desc",
    hasBulb: false,
  },
  {
    numKey: "exp.security.c3.num",
    titleKey: "exp.security.c3.title",
    descKey: "exp.security.c3.desc",
    hasBulb: false,
  },
  {
    numKey: "exp.security.c4.num",
    titleKey: "exp.security.c4.title",
    descKey: "exp.security.c4.desc",
    hasBulb: false,
  },
];

function SecurityCard({ card, className = "" }) {
  const { t } = useTranslation();
  return (
    <div className={`relative bg-[#ee5414] rounded-[20px] flex flex-col justify-between overflow-hidden ${className}`}>
      {card.hasBulb && (
        <div className="absolute inset-0 rounded-[20px] overflow-hidden pointer-events-none opacity-70">
          <img
            src={securityBulb}
            alt=""
            aria-hidden
            className="absolute max-w-none"
            style={{ width: "55.9%", height: "79.76%", left: "47.18%", top: "20.38%" }}
          />
        </div>
      )}
      <span className="relative font-['Montserrat'] font-bold text-[13px] tracking-[1.3px] text-[#ffb393]">
        {t(card.numKey)}
      </span>
      <div className="relative flex flex-col gap-[12px]">
        <h3 className="font-['Montserrat'] font-extrabold text-white leading-normal break-words hyphens-auto"
          style={{ fontSize: "inherit" }}>
          {t(card.titleKey)}
        </h3>
        <p className="font-['Inter'] font-normal text-[#ffb393] leading-[1.4] break-words hyphens-auto"
          style={{ fontSize: "inherit" }}>
          {t(card.descKey)}
        </p>
      </div>
    </div>
  );
}

function SecurityCardMobile({ card }) {
  const { t } = useTranslation();
  return (
    <div className="relative bg-[#ee5414] rounded-[20px] flex flex-col justify-between overflow-hidden h-[248px] px-[20px] py-[40px]">
      {card.hasBulb && (
        <div className="absolute inset-0 rounded-[20px] overflow-hidden pointer-events-none opacity-70">
          <img
            src={securityBulb}
            alt=""
            aria-hidden
            className="absolute max-w-none"
            style={{ width: "50%", height: "80.5%", left: "52.41%", top: "19.49%" }}
          />
        </div>
      )}
      <span className="relative font-['Montserrat'] font-bold text-[13px] tracking-[1.3px] text-[#ffb393]">
        {t(card.numKey)}
      </span>
      <div className="relative flex flex-col gap-[12px]">
        <h3 className="font-['Montserrat'] font-extrabold text-[16px] text-white leading-normal break-words hyphens-auto">
          {t(card.titleKey)}
        </h3>
        <p className="font-['Inter'] font-normal text-[14px] text-[#ffb393] leading-[1.4] break-words hyphens-auto">
          {t(card.descKey)}
        </p>
      </div>
    </div>
  );
}

function Security() {
  const { t } = useTranslation();
  const { link } = useContext(LinkContext);
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="mt bg-[#0a0a0a] py-[60px] xl:py-[160px]">
      <div className="glcontainer">

        {/* ── Mobile ── */}
        <div className="xl:hidden flex flex-col gap-[20px]">
          <h2 className="font-['Montserrat'] font-black text-[24px] text-white tracking-[-0.92px] uppercase leading-none">
            {t("exp.security.title")}
          </h2>

          <Swiper
            slidesPerView={1}
            onSwiper={(swiper) => { swiperRef.current = swiper; }}
            onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
            className="w-full"
          >
            {CARDS.map((card, i) => (
              <SwiperSlide key={i}>
                <SecurityCardMobile card={card} />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Nav pill: arrows + dots */}
          <div className="bg-[#1e1e1e] h-[28px] rounded-[20px] px-[18px] flex items-center justify-between">
            <button
              onClick={() => swiperRef.current?.slidePrev()}
              className="flex items-center justify-center size-[14px] opacity-70"
              aria-label="Previous"
            >
              <img src={securityArrowLeft} alt="" aria-hidden className="size-[10px]" style={{transform: "rotate(-135deg)"}} />
            </button>

            <div className="flex items-center gap-[6px]">
              {CARDS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => swiperRef.current?.slideTo(i)}
                  className={`rounded-full transition-all duration-200 ${
                    i === activeIndex
                      ? "bg-[#ee5414] w-[8px] h-[8px]"
                      : "bg-[#444] w-[6px] h-[6px]"
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={() => swiperRef.current?.slideNext()}
              className="flex items-center justify-center size-[14px] opacity-70"
              aria-label="Next"
            >
              <img src={securityArrowRight} alt="" aria-hidden className="size-[10px]" style={{transform: "rotate(45deg)"}} />
            </button>
          </div>

          <p className="font-['Inter'] font-normal text-[14px] text-[#717171] leading-[1.4]">
            {t("exp.security.desc")}
          </p>
          <AnimatedCTA href={link} mobile full>{t("btn.start-trading")}</AnimatedCTA>
        </div>

        {/* ── Desktop ── */}
        <div className="hidden xl:block relative h-[650px]">
          <h2 className="absolute top-0 left-0 w-[393px] font-['Montserrat'] font-black text-[46px] text-white tracking-[-0.92px] uppercase leading-none z-[1]">
            {t("exp.security.title")}
          </h2>

          <div className="absolute inset-0 flex flex-col gap-[20px] items-end">
            {/* Row 1: right-aligned */}
            <div className="flex gap-[20px] shrink-0">
              <SecurityCard card={CARDS[0]} className="w-[394px] h-[315px] p-[40px] text-[24px]" />
              <SecurityCard card={CARDS[1]} className="w-[393px] h-[315px] p-[40px] text-[24px]" />
            </div>

            {/* Row 2: cards left, desc+btn right */}
            <div className="flex gap-[40px] items-end w-full shrink-0">
              <div className="flex gap-[20px] shrink-0">
                <SecurityCard card={CARDS[2]} className="w-[393px] h-[315px] p-[40px] text-[24px]" />
                <SecurityCard card={CARDS[3]} className="w-[394px] h-[315px] p-[40px] text-[24px]" />
              </div>
              <div className="flex flex-col justify-between h-[315px] w-[373px] shrink-0">
                <p className="font-['Inter'] font-normal text-[18px] text-[#717171] leading-[1.4]">
                  {t("exp.security.desc")}
                </p>
                <AnimatedCTA href={link}>{t("btn.start-trading")}</AnimatedCTA>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

export default Security;
